import * as withAbsintheSocket from "@absinthe/socket";
import type { AbsintheSocket } from "@absinthe/socket";
import type { ClientQuery, ClientSubscribe } from "@codesandbox/create-gql-api";
import { Socket as PhoenixSocket } from "phoenix";
import type { SocketConnectOption } from "phoenix";

import type { SessionApi } from "./SessionApi";
import { createClient } from "./generated_gql";
import type { RESTRequest } from "./types";

const SUBSCRIPTION_HEARTBEAT_INTERVAL = 10_000;

export class GQLApi {
  query: ClientQuery;
  subscribe: ClientSubscribe;

  constructor(options: {
    request: RESTRequest;
    apiUrl: string;
    session: SessionApi;
  }) {
    const WEBSOCKET_URL = new URL(`${options.apiUrl}/socket`);
    const webSocketProtocol =
      WEBSOCKET_URL.protocol === "http:" ? "ws:" : "wss:";
    WEBSOCKET_URL.protocol = webSocketProtocol;

    let socketPromise: Promise<AbsintheSocket> | undefined;

    const client = createClient({
      onRequest: (
        query: string,
        variables?: Record<string, unknown>,
        headers?: Record<string, string>,
      ) => {
        return options
          .request<{ data: Record<string, unknown> }>({
            method: "POST",
            data: { query, variables },
            path: "/graphql",
            headers,
          })
          .then(({ data }) => data);
      },
      onSubscribe: (query, cb, variables) => {
        const currentSocketPromise = socketPromise;

        if (!currentSocketPromise) {
          // This is not really a possible scenario as we always create a socket
          // when starting the first subscription
          return () => {};
        }

        const notifierPromise = currentSocketPromise.then((socket) => {
          const notifier = withAbsintheSocket.send(socket, {
            operation: query,
            variables: {
              // In case variables are undefined
              ...variables,
            },
          });

          withAbsintheSocket.observe(socket, notifier, {
            // eslint-disable-next-line
            onError: console.error,
            onResult: (result) => cb((result as { data: unknown }).data),
          });

          return notifier;
        });

        return () => {
          Promise.all([currentSocketPromise, notifierPromise]).then(
            ([socket, notifier]) => {
              withAbsintheSocket.cancel(socket, notifier);
            },
          );
        };
      },
      includeTypeNames: false,
      cacheQueries: false,
    });
    this.query = client.query.bind(client);

    // Not sure why TS is so angry at the parameters here
    // eslint-disable-next-line
    // @ts-ignore
    this.subscribe = (...args) => {
      // Creating the socket is ASYNC as we potentially need to call an endpoint to get the guardian token
      if (!socketPromise) {
        socketPromise = this.getSubscriptionSocket(
          WEBSOCKET_URL.toString(),
          options.session,
        );
      }

      return client.subscribe(...args);
    };
  }

  private async getSubscriptionSocket(
    websocketUrl: string,
    session: SessionApi,
  ) {
    const getToken = async () =>
      await (session.bearerToken || session.getGuardianToken());
    let token = await getToken();

    const phoenixSocketOpts: Partial<SocketConnectOption> = {
      params: () => ({ guardian_token: token }),
      heartbeatIntervalMs: SUBSCRIPTION_HEARTBEAT_INTERVAL,
    };

    const phoenixSocket = new PhoenixSocket(websocketUrl, phoenixSocketOpts);

    // Whenever the socket errors it is most likely related to an expired token. We go and grab a new token
    // and update it
    phoenixSocket.onError(async () => {
      token = await getToken();
    });

    return withAbsintheSocket.create(phoenixSocket);
  }
}
